/**
 * GridCrash — Local Coin Wallet
 *
 * The offline half of the Clash Coins economy. While the Supabase
 * backend is unreachable, or for rewards that aren't server-authoritative
 * yet (quests, streaks), coins earned and spent land here. Whenever a
 * profile is connected, profile.ts's syncLocalWallet() folds the balance
 * into the server row (clamped per call) — repeatable, not one-shot,
 * since new local coins keep accruing after the first fold.
 *
 * Deliberately dependency-free — profile.ts, catalog.ts, and UI code
 * all import the wallet, never the reverse.
 *
 * Storage: localStorage with in-memory fallback (same pattern as unlocks.ts)
 */

export interface WalletState {
  /** Locally earned, not-yet-synced coin balance. */
  coins: number;
  /** Lifetime coins earned locally (stats only). */
  lifetimeEarned: number;
  /** Item ids purchased locally (unioned with profile.unlocked_assets). */
  unlocked: string[];
  /** Equipped cosmetics for never-online players; profile cache wins when present. */
  equippedTheme: string | null;
  equippedAnimation: string | null;
  /** True once the balance has been folded into the server profile. */
  migrated: boolean;
}

const KEY = "gridcrash.wallet.v1";

let memState: WalletState | null = null;

function defaults(): WalletState {
  return {
    coins: 0,
    lifetimeEarned: 0,
    unlocked: [],
    equippedTheme: null,
    equippedAnimation: null,
    migrated: false,
  };
}

function readState(): WalletState {
  if (memState) return memState;
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<WalletState>;
      memState = {
        coins: typeof parsed.coins === "number" && parsed.coins >= 0 ? Math.floor(parsed.coins) : 0,
        lifetimeEarned: typeof parsed.lifetimeEarned === "number" ? Math.floor(parsed.lifetimeEarned) : 0,
        unlocked: Array.isArray(parsed.unlocked)
          ? parsed.unlocked.filter((i): i is string => typeof i === "string")
          : [],
        equippedTheme: typeof parsed.equippedTheme === "string" ? parsed.equippedTheme : null,
        equippedAnimation: typeof parsed.equippedAnimation === "string" ? parsed.equippedAnimation : null,
        migrated: parsed.migrated === true,
      };
      return memState;
    }
  } catch { /* corrupt or unavailable — fall through */ }
  memState = defaults();
  return memState;
}

function writeState(state: WalletState): void {
  memState = state;
  try { localStorage.setItem(KEY, JSON.stringify(state)); } catch { /* memory fallback holds */ }
}

// ─── Public API ───────────────────────────────────────────────────────────────

export function getWallet(): WalletState {
  return { ...readState(), unlocked: [...readState().unlocked] };
}

/**
 * Credits coins for a finished game, mirroring the server formula:
 * coins = max(1, floor(score / 100)). Call only when the server award
 * was unreachable — never double-credit.
 */
export function creditLocal(score: number): number {
  const coins = Math.max(1, Math.floor(Math.max(0, score) / 100));
  const state = readState();
  state.coins += coins;
  state.lifetimeEarned += coins;
  writeState(state);
  return coins;
}

/** Credits a flat coin bonus (quest/streak rewards). */
export function creditBonus(coins: number): void {
  if (coins <= 0) return;
  const state = readState();
  state.coins += coins;
  state.lifetimeEarned += coins;
  writeState(state);
}

/** Spends from the local balance. False when it can't cover the price. */
export function spendLocal(price: number): boolean {
  const state = readState();
  if (price < 0 || state.coins < price) return false;
  state.coins -= price;
  writeState(state);
  return true;
}

/** Records a locally purchased (or crate-granted) item. */
export function grantLocal(itemId: string): void {
  const state = readState();
  if (!state.unlocked.includes(itemId)) {
    state.unlocked.push(itemId);
    writeState(state);
  }
}

export function isOwnedLocal(itemId: string): boolean {
  return readState().unlocked.includes(itemId);
}

/** Equipped cosmetic for never-online players; profile cache takes priority. */
export function getEquippedLocal(kind: "theme" | "animation"): string | null {
  const state = readState();
  return kind === "theme" ? state.equippedTheme : state.equippedAnimation;
}

export function setEquippedLocal(kind: "theme" | "animation", assetId: string): void {
  const state = readState();
  if (kind === "theme") state.equippedTheme = assetId;
  else state.equippedAnimation = assetId;
  writeState(state);
}

/**
 * Settles a completed fold into the server profile by subtracting the
 * folded amount (not zeroing — coins credited while the fold was in
 * flight survive). Unlocks stay — they're unioned, not moved.
 */
export function markMigrated(foldedCoins: number): void {
  const state = readState();
  state.coins = Math.max(0, state.coins - Math.max(0, foldedCoins));
  state.migrated = true;
  writeState(state);
}
